import { Globe, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { getLandingUrl } from '../lib/urls';

import { navigationCategories } from './navigation';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileDrawer({ isOpen, onClose }: MobileDrawerProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label={t('Navegación principal')}>
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-black/65 backdrop-blur-sm"
        onClick={onClose}
        aria-label={t('Cerrar menú')}
      />
      <aside className="relative flex h-full w-[min(88vw,21rem)] flex-col border-r border-border bg-[hsl(var(--surface-subtle))] shadow-2xl">
        <div className="flex h-[var(--topbar-height)] shrink-0 items-center justify-between border-b border-border px-5">
          <Link to="/dashboard" onClick={onClose}>
            <img src="/logo.png" alt="SUSCRI.net" className="h-9 w-auto" />
          </Link>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground transition hover:bg-accent hover:text-foreground"
          >
            <span className="sr-only">{t('Cerrar menú')}</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <nav className="scrollbar-subtle flex-1 overflow-y-auto px-1 pb-4 pt-2">
          {navigationCategories.map(category => (
            <div key={category.name} className="mb-2">
              <div className="nav-category">{t(category.name)}</div>
              <ul role="list" className="space-y-1">
                {category.items.map(item => {
                  const isActive = location.pathname === item.href;
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        onClick={onClose}
                        aria-current={isActive ? 'page' : undefined}
                        className={isActive ? 'nav-item-active' : 'nav-item'}
                      >
                        <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                        {t(item.name)}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-t border-border p-3">
          <a href={getLandingUrl()} className="nav-item" onClick={onClose}>
            <Globe className="h-5 w-5 shrink-0" aria-hidden="true" />
            {t('Página principal')}
          </a>
        </div>
      </aside>
    </div>
  );
}
